import React, { forwardRef } from 'react'
import {
  sanitizeContentType,
  useSearchAnalytics,
} from '../../pages/SearchPage/analytics'
import { clearFiltersEvent } from '../../pages/SearchPage/events'
import {
  getClearFiltersEventLabel,
  getNoResultsClearFiltersEventLabel,
} from '../../store/helpers'
import { Chip } from '@moonpig/launchpad-components'
import { useFindLocaleText } from '../../text-localisation'
import { useSearchStore } from '../../store/SearchStore'
import { ApplicableFilter } from '../../services/types/services'
import { CarouselItem } from '../FiltersCarousel/components'
import { FilterSource } from '../../types'

type FilterChipProps = {
  filter: ApplicableFilter
  select: boolean
  source: FilterSource
}

const buildAriaLabel = (
  localiseText: ReturnType<typeof useFindLocaleText>,
  filter: ApplicableFilter,
  select: boolean,
) => {
  const group = filter.parent
  if (filter.__typename === 'IntegerFilter') {
    return localiseText('find.remove_facet_group_filter_facet_label', {
      group: filter.label,
      label: `${filter.selectedValueLabel ?? /* istanbul ignore next */ ''}`,
    })
  }
  return select
    ? localiseText('find.apply_facet_group_filter_facet_label', {
        group,
        label: filter.label.toLowerCase(),
      })
    : localiseText('find.remove_facet_group_filter_facet_label', {
        group,
        label: filter.label,
      })
}

export const FilterChip = forwardRef<HTMLLIElement, FilterChipProps>(
  ({ filter, select, source }, ref) => {
    const isLaunchpadAnalytics = useSearchAnalytics()

    const pageType = useSearchStore(store => store.pageContext.type)

    const isClearAction =
      source === FilterSource.NoSearchResults ||
      filter.__typename === 'IntegerFilter'

    const clearLabel =
      filter.__typename === 'IntegerFilter' &&
      source !== FilterSource.NoSearchResults
        ? getClearFiltersEventLabel([{ category: filter, source }])
        : getNoResultsClearFiltersEventLabel([{ filter }])

    // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
    const clearClickEvent = clearFiltersEvent()
      .withEventData({
        category: `${pageType} action`.toLowerCase(),
        label: clearLabel.toLowerCase(),
      })
      .withContentData({
        content_type: sanitizeContentType(
          `${pageType} action | clear filters | ${clearLabel}`,
        ),
      })

    const localiseText = useFindLocaleText()
    const toggleFilter = useSearchStore(store => store.toggleFilter)
    const clearFiltersWhenNoResults = useSearchStore(
      store => store.clearFiltersWhenNoResults,
    )
    const clearFilters = useSearchStore(store => store.clearFilters)

    const ariaLabel = buildAriaLabel(localiseText, filter, select)

    const displayLabel =
      filter.__typename === 'IntegerFilter'
        ? filter.selectedValueLabel
        : filter.label

    const handleClick = () => {
      if (source === FilterSource.NoSearchResults) {
        clearFiltersWhenNoResults({
          filter,
        })
      } else if (filter.__typename === 'IntegerFilter') {
        clearFilters({ category: filter, source })
      } else {
        toggleFilter({
          filter,
          select,
          source,
        })
      }
    }

    return (
      <CarouselItem ref={ref}>
        <Chip
          variant="low-emphasis"
          dismissible
          selected={!select}
          events={
            isLaunchpadAnalytics && isClearAction
              ? { onClick: clearClickEvent }
              : undefined
          }
          onClick={handleClick}
          aria-label={ariaLabel}
        >
          {displayLabel}
        </Chip>
      </CarouselItem>
    )
  },
)
